import React from "react"
import {
  Box, Divider, Flex, ListItem, Stack, Switch, Text, UnorderedList,
} from "@chakra-ui/react"
import { Control, useController } from "react-hook-form"
import { useQueryClient } from "react-query"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import Link from "components/elements/Link"
import { useCurrentEvent } from "queries/events"
import { putEvent } from "api/events"
import { setCurrentEvent } from "context/actions"
import { useCurrentUser } from "queries/users"
import {
  WHATSAPP_FALLBACK_TO_SMS_ARTICLE_LINK,
  WHATSAPP_OPT_IN_PROCESS_ARTICLE_LINK,
} from "utilities/externalLinks"
import { handleError, successToast } from "utilities/toasts"

type Props = {
  onClose: () => void
}

type FormValues = {
  autoOptInEnabled: boolean
  smsFallbackEnabled: boolean
}

const WhatsAppSendingOptions = ({ onClose }: Props) => {
  const { data: event } = useCurrentEvent()
  const currentUser = useCurrentUser()
  const queryClient = useQueryClient()

  const initialValues: FormValues = {
    autoOptInEnabled: event?.autoOptInEnabled ?? true,
    smsFallbackEnabled: event?.smsFallbackEnabled ?? true,
  }

  const onSubmit = async (values: FormValues) => {
    if (!event || !currentUser) return
    try {
      const { data } = await putEvent(event.id, { ...event, ...values })
      setCurrentEvent(queryClient, data, currentUser)
      successToast({ title: "Sending options updated" })
      onClose()
    } catch (error) {
      handleError(error)
    }
  }

  return (
    <ModalForm
      title="WhatsApp Sending Options"
      size="lg"
      initialValues={initialValues}
      onClose={onClose}
      onSubmit={onSubmit}
      submitText="Save"
      mode="onChange"
      disableSubmitWhenNotDirty
    >
      {({ control }) => (
        <Stack spacing={6}>
          <Text>
            These settings apply to all WhatsApp messages in this event.
          </Text>

          <Stack spacing={3}>
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontSize="lg" fontWeight="semibold">
                Send Automatic Opt-In Messages
              </Text>
              <ControlledSwitch name="autoOptInEnabled" control={control} />
            </Flex>
            <Text>
              Automatically send an opt-in message to guests who haven&apos;t
              opted in yet. If they approve within 48 hours, your original
              message is sent immediately after.
            </Text>
            <Text fontStyle="italic">
              Note: Each opt-in message uses 1 additional credit.
            </Text>
            <Box>
              Learn more:{" "}
              <Link href={WHATSAPP_OPT_IN_PROCESS_ARTICLE_LINK} target="_blank">
                WhatsApp Opt-In Process
              </Link>
            </Box>
          </Stack>

          <Divider />

          <Stack spacing={3}>
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontSize="lg" fontWeight="semibold">
                Fallback to SMS
              </Text>
              <ControlledSwitch name="smsFallbackEnabled" control={control} />
            </Flex>
            <Text>
              If a WhatsApp message cannot be delivered, resend it via SMS.
            </Text>
            <Box>
              <Text mb={1}>This ensures delivery when:</Text>
              <UnorderedList pl={4} spacing={1}>
                <ListItem>The phone number is not registered on WhatsApp</ListItem>
                <ListItem>You are sending a marketing message to a U.S. phone number</ListItem>
                <ListItem>The guest cannot receive WhatsApp messages due
                  to regional or platform restrictions
                </ListItem>
              </UnorderedList>
            </Box>
            <Text fontStyle="italic">
              Note: Each fallback SMS uses 1 additional credit.
            </Text>
            <Box>
              Learn more:{" "}
              <Link href={WHATSAPP_FALLBACK_TO_SMS_ARTICLE_LINK} target="_blank">
                WhatsApp Fallback to SMS
              </Link>
            </Box>
          </Stack>
        </Stack>
      )}
    </ModalForm>
  )
}

type ControlledSwitchProps = {
  name: keyof FormValues
  control: Control<FormValues>
}

const ControlledSwitch = ({ name, control }: ControlledSwitchProps) => {
  const { field } = useController({ name, control })

  return (
    <Switch
      size="lg"
      isChecked={Boolean(field.value)}
      onChange={(e) => field.onChange(e.target.checked)}
    />
  )
}

export default WhatsAppSendingOptions
